/**
 * Draggable Marquee
 * Uses GSAP and Draggable for smooth scrolling marquee effect
 */
if(window.innerWidth < 767){
  
  // Initialize when DOM is ready and GSAP is loaded
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initWhenReady);
  } else {
    initWhenReady();
  }

  function initWhenReady() {
    // Wait for GSAP to be available
    if (typeof gsap === 'undefined' || typeof Draggable === 'undefined') {
      // Try again after a short delay
      let attempts = 0;
      const checkGSAP = setInterval(() => {
        attempts++;
        if (typeof gsap !== 'undefined' && typeof Draggable !== 'undefined') {
          clearInterval(checkGSAP);
          initDraggableMarquee();
        } else if (attempts > 20) {
          clearInterval(checkGSAP);
        }
      }, 100);
    } else {
      // GSAP is ready, initialize
      setTimeout(initDraggableMarquee, 100);
    }
  }

  function initDraggableMarquee() {
    const sections = document.querySelectorAll('.section-draggable-marquee');
    if (!sections.length) return;

    sections.forEach(section => {
      // Get DOM elements
      const container = section.querySelector('.draggable-marquee');
      const wrapper = section.querySelector('.draggable-marquee__wrapper');
      const itemsList = section.querySelector('.draggable-marquee__items');
      const items = section.querySelectorAll('.draggable-marquee__item');
      const prevBtn = section.querySelector('.draggable-marquee__arrow--prev');
      const nextBtn = section.querySelector('.draggable-marquee__arrow--next');

      if (!container || !itemsList || items.length === 0) {
        console.error('Missing required elements for draggable marquee');
        return;
      }

      // Determine if we're on mobile
      const isMobile = window.innerWidth <= 768;
      const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      
      // Simple drag-only implementation for mobile
      if (isMobile || isTouchDevice) {
        // Make the wrapper scrollable
        wrapper.style.overflowX = 'auto';
        wrapper.style.overflowY = 'hidden';
        wrapper.style.webkitOverflowScrolling = 'touch';
        wrapper.style.scrollBehavior = 'smooth';
        
        // Hide scrollbar
        const style = document.createElement('style');
        style.textContent = `
          .draggable-marquee__wrapper::-webkit-scrollbar {
            display: none;
          }
          .draggable-marquee__wrapper {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }
        `;
        document.head.appendChild(style);
        
        // Add snap scrolling for better UX
        wrapper.style.scrollSnapType = 'x mandatory';
        items.forEach(item => {
          item.style.scrollSnapAlign = 'start';
        });
        
        return; // Exit early for mobile - use native scrolling
      }
      
      // Desktop implementation with GSAP draggable
      // Disable CSS transitions on items to prevent the "coming back" effect
      items.forEach(item => {
        gsap.set(item, { 
          clearProps: "transition",
          css: { 
            transition: "none"
          }
        });
      });
      
      // Create the marquee instance using horizontalLoop for desktop only
      const marquee = horizontalLoop(items, {
        repeat: 0,
        speed: 1,
        draggable: true,
        reversed: false,
        paddingRight: parseFloat(getComputedStyle(itemsList).gap || 20),
        paused: true,
        container: container
      });

      // Always keep the marquee paused - no auto-scrolling
      gsap.set(marquee, { timeScale: 0 });

      // Ensure links work properly by preventing draggable interference
      items.forEach(item => {
        const links = item.querySelectorAll('a');
        links.forEach(link => {
          // Prevent default drag behavior on links
          link.addEventListener('dragstart', (e) => e.preventDefault());
          
          link.addEventListener('click', (e) => {
            // Stop propagation to prevent draggable from interfering
            e.stopPropagation();
            // Let the link work normally - it will respect the target attribute
          });
          
          // Add touch feedback for mobile
          if (isTouchDevice) {
            link.addEventListener('touchstart', () => {
              item.classList.add('touch-active');
            }, { passive: true });
            
            link.addEventListener('touchend', () => {
              setTimeout(() => item.classList.remove('touch-active'), 100);
            }, { passive: true });
          }
        });
      });

      // Add button event listeners
      if (prevBtn) {
        prevBtn.addEventListener('click', (e) => {
          e.preventDefault();
          // Get current progress and move to previous item
          const currentIndex = marquee.current();
          const targetIndex = currentIndex - 1;
          
          // Navigate to previous item
          marquee.toIndex(targetIndex, {
            duration: 0.6, 
            ease: "power2.inOut",
            onComplete: () => {
              // Update the current index after animation
              marquee.updateIndex();
            }
          });
        });
      }

      if (nextBtn) {
        nextBtn.addEventListener('click', (e) => {
          e.preventDefault();
          // Get current progress and move to next item
          const currentIndex = marquee.current();
          const targetIndex = currentIndex + 1;
          
          // Navigate to next item
          marquee.toIndex(targetIndex, {
            duration: 0.6, 
            ease: "power2.inOut",
            onComplete: () => {
              // Update the current index after animation
              marquee.updateIndex();
            }
          });
        });
      }

      // Store the marquee instance on the container
      container.marqueeInstance = marquee;
      
      // Keep marquee completely paused - no auto-scrolling at all
      gsap.set(marquee, { timeScale: 0 });
      
    });
  }

  /**
   * Creates a horizontally looping animation using GSAP
   * @param {Array|String} items - The elements to animate
   * @param {Object} config - Configuration options
   * @returns {gsap.timeline} The animation timeline with additional methods
   */
  function horizontalLoop(items, config) {
    items = gsap.utils.toArray(items);
    config = config || {};
    let tl = gsap.timeline({
        repeat: 0, // No repeat - we don't want looping animation
        paused: true, // Always paused
        defaults: { ease: "none" },
        onReverseComplete: () => tl.totalTime(tl.rawTime() + tl.duration() * 100)
      }),
      length = items.length,
      startX = items[0].offsetLeft,
      times = [],
      widths = [],
      xPercents = [],
      curIndex = 0,
      pixelsPerSecond = (config.speed || 1) * 100,
      snap = config.snap === false ? (v) => v : gsap.utils.snap(config.snap || 1),
      populateWidths = () =>
        items.forEach((el, i) => {
          widths[i] = parseFloat(gsap.getProperty(el, "width", "px"));
          xPercents[i] = snap(
            (parseFloat(gsap.getProperty(el, "x", "px")) / widths[i]) * 100 +
              gsap.getProperty(el, "xPercent")
          );
        }),
      getTotalWidth = () =>
        items[length - 1].offsetLeft +
        (xPercents[length - 1] / 100) * widths[length - 1] -
        startX +
        items[length - 1].offsetWidth *
          gsap.getProperty(items[length - 1], "scaleX") +
        (parseFloat(config.paddingRight) || 0),
      totalWidth,
      curX,
      distanceToStart,
      distanceToLoop,
      item,
      i;
    
    populateWidths();
    gsap.set(items, {
      // convert "x" to "xPercent" to make things responsive, and populate the widths/xPercents Arrays to make lookups faster.
      xPercent: (i) => xPercents[i]
    });
    gsap.set(items, { x: 0 });
    totalWidth = getTotalWidth();
    
    for (i = 0; i < length; i++) {
      item = items[i];
      curX = (xPercents[i] / 100) * widths[i];
      distanceToStart = item.offsetLeft + curX - startX;
      distanceToLoop =
        distanceToStart + widths[i] * gsap.getProperty(item, "scaleX");
      tl.to(
        item,
        {
          xPercent: snap(((curX - distanceToLoop) / widths[i]) * 100),
          duration: distanceToLoop / pixelsPerSecond
        },
        0
      )
        .fromTo(
          item,
          {
            xPercent: snap(
              ((curX - distanceToLoop + totalWidth) / widths[i]) * 100
            )
          },
          {
            xPercent: xPercents[i],
            duration:
              (curX - distanceToLoop + totalWidth - curX) / pixelsPerSecond,
            immediateRender: false
          },
          distanceToLoop / pixelsPerSecond
        )
        .add("label" + i, distanceToStart / pixelsPerSecond);
      times[i] = distanceToStart / pixelsPerSecond;
    }
    
    function toIndex(index, vars) {
      vars = vars || {};
      Math.abs(index - curIndex) > length / 2 &&
        (index += index > curIndex ? -length : length); // always go in the shortest direction
      let newIndex = gsap.utils.wrap(0, length, index),
        time = times[newIndex];
      if (time > tl.time() !== index > curIndex) {
        // if we're wrapping the timeline's playhead, make the proper adjustments
        vars.modifiers = { time: gsap.utils.wrap(0, tl.duration()) };
        time += tl.duration() * (index > curIndex ? 1 : -1);
      }
      curIndex = newIndex;
      vars.overwrite = true;
      return tl.tweenTo(time, vars);
    }
    
    tl.next = (vars) => toIndex(curIndex + 1, vars);
    tl.previous = (vars) => toIndex(curIndex - 1, vars);
    tl.current = () => curIndex;
    tl.toIndex = (index, vars) => toIndex(index, vars);
    tl.updateIndex = () =>
      (curIndex = Math.round(tl.progress() * (items.length - 1)));
    tl.times = times;
    tl.progress(1, true).progress(0, true); // pre-render for performance
    
    if (config.reversed) {
      tl.vars.onReverseComplete();
      tl.reverse();
    }
    
    if (config.draggable && typeof Draggable === "function") {
      let proxy = document.createElement("div"),
        wrap = gsap.utils.wrap(0, 1),
        ratio,
        startProgress,
        draggable,
        dragSnap,
        roundFactor,
        // Throttle align with rAF to reduce jank on touch devices
        rafId,
        align = () =>
          tl.progress(
            wrap(startProgress + (draggable.startX - draggable.x) * ratio)
          ),
        alignRaf = () => {
          if (rafId) return;
          rafId = requestAnimationFrame(() => {
            align();
            rafId = null;
          });
        },
        syncIndex = () => tl.updateIndex();
      
      const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      
      // Configure snap: disable on touch to avoid sticky jumps, keep on desktop
      draggable = Draggable.create(proxy, {
        // Use the marquee container so dragging works anywhere over the strip
        trigger: config.container || items[0].parentNode,
        type: "x",
        edgeResistance: 0.35,
        // Avoid accidental drags; require a small horizontal intent
        minimumMovement: 5,
        dragResistance: 0.12,
        // Only engage when horizontal movement is dominant
        lockAxis: true,
        // Allow dragging even when starting on links/buttons; real clicks still work if no movement
        dragClickables: true,
        // Cooperate with native scrolling to avoid gesture deadlocks on some devices
        allowNativeTouchScrolling: true,
        // Keep clicks functional by not hijacking default behavior when no drag occurs
        onPress() {
          startProgress = tl.progress();
          tl.progress(0);
          populateWidths();
          totalWidth = getTotalWidth();
          ratio = 1 / totalWidth;
          dragSnap = totalWidth / items.length;
          roundFactor = Math.pow(
            10,
            ((dragSnap + "").split(".")[1] || "").length
          );
          tl.progress(startProgress);
          // Mark dragging state to avoid accidental link taps
          if (config.container) {
            config.container.classList.add('is-dragging');
          }
        },
        onDrag() {
          alignRaf();
        },
        onThrowUpdate() {
          alignRaf();
        },
        inertia: true,
        snap: isTouchDevice ? false : ((value) => {
          let n = Math.round(parseFloat(value) / dragSnap) * dragSnap * roundFactor;
          return (n - (n % 1)) / roundFactor;
        }),
        onRelease() {
          syncIndex();
          // Small timeout lets click cancellation propagate correctly
          if (config.container) {
            setTimeout(() => config.container.classList.remove('is-dragging'), 0);
          }
        },
        onThrowComplete() {
          gsap.set(proxy, { x: 0 });
          syncIndex();
          if (config.container) {
            config.container.classList.remove('is-dragging');
          }
        }
      })[0];
      
      // Enhance inertia for touch devices
      if (isTouchDevice && draggable) {
        // Override the draggable instance properties for better mobile performance
        if (draggable.vars && draggable.vars.inertia) {
          draggable.vars.inertia = {
            resistance: 40,
            velocityScale: 2.25
          };
        }
        
        // Add touch-specific CSS for better performance
        gsap.set(items[0].parentNode, {
          webkitUserSelect: "none",
          userSelect: "none",
          webkitTouchCallout: "none"
        });
      }
    }

    return tl;
  }







}